import type { ExtractedQuestion } from '@/types/extractedQuestions';

export type Segment =
  | { kind: 'plain'; text: string }
  | { kind: 'highlight'; text: string; questionIndex: number };

export type ComputeSegmentsResult = {
  segments: Segment[];
  /** Indices that were dropped from inline marks (overlapping spans). They
   * still appear as cards in the expander, but have no inline anchor. */
  droppedQuestionIndices: number[];
  /** True when at least one span's `end` exceeded `body.length` and we
   * fell back to plain rendering for the entire body. */
  fallbackUsed: boolean;
};

/**
 * Splits `messageBody` into a sequence of plain and highlighted segments
 * based on `extractedQuestions` source spans.
 *
 * Rules:
 * - Questions with null `sourceStartIndex` or `sourceEndIndex` are skipped
 *   (still rendered as cards by the expander, but no inline anchor).
 * - Spans are sorted by `sourceStartIndex`; later overlapping spans are
 *   dropped from inline marks.
 * - If any span's `end` exceeds `body.length`, fall back to a single plain
 *   segment for the whole body.
 */
export function computeSegments(
  body: string,
  extractedQuestions: ExtractedQuestion[]
): ComputeSegmentsResult {
  if (extractedQuestions.length === 0) {
    return {
      segments: [{ kind: 'plain', text: body }],
      droppedQuestionIndices: [],
      fallbackUsed: false,
    };
  }

  const candidates = extractedQuestions
    .map((question, questionIndex) => ({ question, questionIndex }))
    .filter(
      ({ question }) => question.sourceStartIndex !== null && question.sourceEndIndex !== null
    );

  if (candidates.length === 0) {
    return {
      segments: [{ kind: 'plain', text: body }],
      droppedQuestionIndices: [],
      fallbackUsed: false,
    };
  }

  for (const { question } of candidates) {
    const start = question.sourceStartIndex as number;

    const end = question.sourceEndIndex as number;

    if (start < 0 || end > body.length || end <= start) {
      return {
        segments: [{ kind: 'plain', text: body }],
        droppedQuestionIndices: [],
        fallbackUsed: true,
      };
    }
  }

  const sorted = candidates
    .slice()
    .sort(
      (a, b) => (a.question.sourceStartIndex as number) - (b.question.sourceStartIndex as number)
    );

  const accepted: { questionIndex: number; start: number; end: number }[] = [];

  const dropped: number[] = [];

  let lastAcceptedEnd = -1;

  for (const { question, questionIndex } of sorted) {
    const start = question.sourceStartIndex as number;

    const end = question.sourceEndIndex as number;

    if (start < lastAcceptedEnd) {
      dropped.push(questionIndex);

      continue;
    }

    accepted.push({ questionIndex, start, end });

    lastAcceptedEnd = end;
  }

  const segments: Segment[] = [];

  let cursor = 0;

  for (const span of accepted) {
    if (span.start > cursor) {
      segments.push({ kind: 'plain', text: body.slice(cursor, span.start) });
    }

    segments.push({
      kind: 'highlight',
      text: body.slice(span.start, span.end),
      questionIndex: span.questionIndex,
    });

    cursor = span.end;

    cursor = skipOrdinalMarker(body, cursor, span.questionIndex);
  }

  if (cursor < body.length) {
    segments.push({ kind: 'plain', text: body.slice(cursor) });
  }

  return {
    segments,
    droppedQuestionIndices: dropped,
    fallbackUsed: false,
  };
}

/**
 * Skips a leading ` Q{n+1} ` ordinal marker in the body immediately after a
 * highlight span. Defensive against test data or upstream tooling that
 * inlines the chip ordinal as literal text — the chip component renders
 * `Q{n+1}` itself, so leaving the literal in the plain segment would
 * produce visible duplication.
 *
 * Tolerates one or two leading whitespace chars and an optional trailing
 * whitespace. No-op when the next chars do not match the expected pattern.
 */
function skipOrdinalMarker(body: string, fromIndex: number, questionIndex: number): number {
  const ordinal = `Q${questionIndex + 1}`;

  const slice = body.slice(fromIndex);

  const match = slice.match(/^[ \t]{0,2}(Q\d+)[ \t]{0,2}/);

  if (!match || match[1] !== ordinal) {
    return fromIndex;
  }

  return fromIndex + match[0].length;
}
